import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription, interval } from 'rxjs';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { DividerModule } from 'primeng/divider';
import { ProgressBarModule } from 'primeng/progressbar';
import {
  ComplaintApiService,
  ExecutionMetadata,
  ExecutionStatusResponse,
} from '../../services/complaint-api.service';

@Component({
  selector: 'app-complaint-import-active',
  standalone: true,
  imports: [CommonModule, CardModule, TagModule, DividerModule, ProgressBarModule],
  template: `
    <p-card>
      <ng-template pTemplate="header">
        <h2 style="padding: 1rem 1rem 0 1rem; margin: 0;">
          <i class="pi pi-sync" [class.pi-spin]="!!activeMeta"></i>
          Importações Ativas
        </h2>
      </ng-template>

      <ng-template pTemplate="content">
        <!-- Nenhuma ativa -->
        <div *ngIf="!activeMeta" class="empty-state">
          <i class="pi pi-info-circle" style="font-size: 2rem;"></i>
          <p>Não há importações ativas no momento.</p>
        </div>

        <!-- Importação ativa -->
        <div *ngIf="activeMeta as meta" class="active-section">

          <!-- Status + Tag -->
          <div class="status-row">
            <span class="label">Status</span>
            <p-tag
              [value]="meta.completed ? 'CONCLUÍDA' : 'EM ANDAMENTO'"
              [severity]="meta.completed ? 'success' : 'info'">
            </p-tag>
          </div>

          <p-divider></p-divider>

          <!-- Detalhes -->
          <div class="details-grid">
            <div class="detail">
              <span class="detail-label">Tipo</span>
              <span class="detail-value">{{ meta.type }}</span>
            </div>
            <div class="detail">
              <span class="detail-label">Início</span>
              <span class="detail-value">{{ meta.startTime | date:'dd/MM/yyyy HH:mm:ss' }}</span>
            </div>
            <div class="detail" *ngIf="meta.endTime">
              <span class="detail-label">Término</span>
              <span class="detail-value">{{ meta.endTime | date:'dd/MM/yyyy HH:mm:ss' }}</span>
            </div>
            <div class="detail">
              <span class="detail-label">Total Salvo</span>
              <span class="detail-value">{{ meta.totalSaved }}</span>
            </div>
            <div class="detail" *ngIf="durationSec != null">
              <span class="detail-label">Duração</span>
              <span class="detail-value">{{ durationSec }}s</span>
            </div>
          </div>

          <!-- Parâmetros -->
          <div class="params-section" *ngIf="(meta.parameters | keyvalue)?.length">
            <p-divider></p-divider>
            <span class="label">Parâmetros</span>
            <div class="details-grid">
              <div class="detail" *ngFor="let p of meta.parameters | keyvalue">
                <span class="detail-label">{{ p.key }}</span>
                <span class="detail-value">{{ p.value }}</span>
              </div>
            </div>
          </div>

          <!-- Barra de progresso percentual -->
          <div *ngIf="!meta.completed" class="progress-section mt-3">
            <p-divider></p-divider>
            <span class="label mb-1">Progresso da Importação</span>

            <!-- Com dados de progresso -->
            <ng-container *ngIf="progressInfo as pi; else indeterminateBar">
              <p-progressBar
                [value]="pi.percentage"
                [showValue]="true"
                unit="%"
                mode="determinate"
                [style]="{ height: '20px' }">
              </p-progressBar>
              <div class="progress-details mt-1">
                <span class="progress-sub">{{ pi.processed }} / {{ pi.total }} registros processados</span>
                <span class="progress-msg" *ngIf="pi.message">&middot; {{ pi.message }}</span>
              </div>
            </ng-container>

            <!-- Sem dados de progresso (indeterminado) -->
            <ng-template #indeterminateBar>
              <p-progressBar
                mode="indeterminate"
                [style]="{ height: '20px' }">
              </p-progressBar>
              <div class="progress-details mt-1">
                <span class="progress-sub">Aguardando dados do servidor...</span>
              </div>
            </ng-template>
          </div>
        </div>
      </ng-template>
    </p-card>
  `,
  styles: [`
    .empty-state {
      display: flex; align-items: center; gap: 0.75rem;
      padding: 1.5rem; color: var(--text-color-secondary, #64748b); font-size: 1rem;
    }
    .empty-state p { margin: 0; }
    .status-row { display: flex; align-items: center; gap: 0.75rem; }
    .active-section { padding: 0.5rem 0; }
    .label { font-weight: 700; font-size: 0.95rem; }
    .mb-1 { display: block; margin-bottom: 0.5rem; }
    .mt-1 { margin-top: 0.5rem; }
    .mt-3 { margin-top: 1.5rem; }
    .details-grid {
      display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 0.75rem;
    }
    .detail { display: flex; flex-direction: column; gap: 0.15rem; }
    .detail-label {
      font-size: 0.75rem; font-weight: 600;
      color: var(--text-color-secondary, #64748b); text-transform: uppercase;
    }
    .detail-value { font-size: 0.9rem; font-weight: 500; }
    .params-section { margin-top: 0.5rem; }
    .progress-section {
      background: var(--surface-ground, #f8f9fa);
      border-radius: 8px; padding: 1rem;
    }
    .progress-details {
      display: flex; align-items: center; gap: 0.25rem; flex-wrap: wrap;
    }
    .progress-sub { font-size: 0.85rem; color: var(--text-color-secondary, #64748b); }
    .progress-msg { font-size: 0.85rem; color: var(--text-color, #333); }
    :host ::ng-deep .p-card .p-card-body { padding: 0; }
  `]
})
export class ComplaintImportActiveComponent implements OnInit, OnDestroy {

  activeMeta: ExecutionMetadata | null = null;
  activeExecutionId: string | null = null;
  progressInfo: { processed: number; total: number; percentage: number; message: string } | null = null;
  durationSec: number | null = null;

  private pollSub?: Subscription;

  constructor(private api: ComplaintApiService) {}

  ngOnInit(): void {
    this.fetchAll();
    this.pollSub = interval(5000).subscribe(() => this.fetchAll());
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
  }

  private fetchAll(): void {
    this.api.getActiveExecutions().subscribe({
      next: (res) => {
        if (res.success && res.executions) {
          // Encontra a primeira execução ativa (não concluída)
          const entries = Object.entries(res.executions);
          const activeEntry = entries.find(([, meta]) => !meta.completed);
          if (activeEntry) {
            const [execId, meta] = activeEntry;
            this.activeMeta = meta;
            this.activeExecutionId = execId;
            // Busca status detalhado com progresso
            this.fetchStatus(execId);
          } else {
            this.activeMeta = null;
            this.activeExecutionId = null;
            this.progressInfo = null;
          }
        } else {
          this.activeMeta = null;
          this.activeExecutionId = null;
          this.progressInfo = null;
        }
      },
      error: () => { this.activeMeta = null; this.progressInfo = null; },
    });
  }

  private fetchStatus(executionId: string): void {
    this.api.getExecutionStatus(executionId).subscribe({
      next: (status: ExecutionStatusResponse) => {
        if (status.success) {
          // Atualiza metadados com dados mais frescos
          if (this.activeMeta) {
            this.activeMeta = {
              ...this.activeMeta,
              completed: status.completed,
              totalSaved: status.totalSaved,
              endTime: status.endTime ?? this.activeMeta.endTime,
            };
          }
          this.durationSec = status.durationSeconds ?? null;
          this.progressInfo = status.currentProgress ?? null;
        }
      },
      error: () => { this.progressInfo = null; },
    });
  }
}
